import React, { useEffect, useRef, useState } from 'react';
import { bool } from 'prop-types';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';

import {
  PAGE_LOAD_INDICATOR_HIDE,
  PAGE_LOAD_INDICATOR_SHOW,
  hidePageLoadIndicator,
  showPageLoadIndicator,
} from '../../util/pageLoadIndicator';

import css from './PageLoadIndicator.module.css';

const LOADER_SRC = '/static/promotix-page-loader.gif';

const isPlainLeftClick = event =>
  event.button === 0 && !event.metaKey && !event.altKey && !event.ctrlKey && !event.shiftKey;

const shouldShowForAnchorClick = event => {
  if (!isPlainLeftClick(event)) return false;

  const target = event.target;
  const anchor = target && typeof target.closest === 'function' ? target.closest('a[href]') : null;
  if (!anchor) return false;
  if (anchor.target && anchor.target !== '_self') return false;
  if (anchor.hasAttribute('download')) return false;

  const href = anchor.getAttribute('href') || '';
  if (/^(mailto:|tel:|sms:|javascript:)/i.test(href)) return false;

  try {
    const url = new URL(anchor.href, window.location.href);
    if (url.origin !== window.location.origin) return false;

    const current = window.location;
    const onlyHashChanged =
      url.pathname === current.pathname && url.search === current.search && url.hash !== current.hash;
    const sameLocation =
      url.pathname === current.pathname && url.search === current.search && url.hash === current.hash;

    return !onlyHashChanged && !sameLocation;
  } catch (e) {
    return false;
  }
};

export const PageLoadIndicatorInline = () => (
  <div className={classNames(css.inline)} role="status" aria-live="polite">
    <img className={css.image} src={LOADER_SRC} alt="" />
    <span className={css.visuallyHidden}>Loading</span>
  </div>
);

const PageLoadIndicator = props => {
  const { watchNavigationClicks } = props;
  const [visible, setVisible] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const autoHideTimerRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const clearAutoHideTimer = () => {
      if (autoHideTimerRef.current) {
        window.clearTimeout(autoHideTimerRef.current);
        autoHideTimerRef.current = null;
      }
    };
    const onShow = event => {
      clearAutoHideTimer();
      setImageFailed(false);
      setVisible(true);
      const detail = event.detail || {};
      if (!detail.persist) {
        autoHideTimerRef.current = window.setTimeout(() => setVisible(false), detail.timeout || 15000);
      }
    };
    const onHide = () => {
      clearAutoHideTimer();
      setVisible(false);
    };
    window.addEventListener(PAGE_LOAD_INDICATOR_SHOW, onShow);
    window.addEventListener(PAGE_LOAD_INDICATOR_HIDE, onHide);
    return () => {
      clearAutoHideTimer();
      window.removeEventListener(PAGE_LOAD_INDICATOR_SHOW, onShow);
      window.removeEventListener(PAGE_LOAD_INDICATOR_HIDE, onHide);
    };
  }, []);

  useEffect(() => {
    if (!watchNavigationClicks) return undefined;
    const onClick = event => {
      if (shouldShowForAnchorClick(event)) {
        showPageLoadIndicator({ timeout: 15000 });
      }
    };
    document.addEventListener('click', onClick, false);
    return () => {
      document.removeEventListener('click', onClick, false);
    };
  }, [watchNavigationClicks]);

  useEffect(() => {
    hidePageLoadIndicator();
  }, [location.pathname, location.search, location.hash]);

  return visible ? (
    <div className={css.overlay} role="status" aria-live="polite" aria-busy="true">
      {imageFailed ? (
        <span className={css.fallbackText}>Loading...</span>
      ) : (
        <img className={css.image} src={LOADER_SRC} alt="" onError={() => setImageFailed(true)} />
      )}
      <span className={css.visuallyHidden}>Loading</span>
    </div>
  ) : null;
};

PageLoadIndicator.defaultProps = {
  watchNavigationClicks: true,
};

PageLoadIndicator.propTypes = {
  watchNavigationClicks: bool,
};

export default PageLoadIndicator;
